import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, 
  Clock,
  User,
  ChevronRight,
  Users,
  RefreshCw
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import CreateTaskModal from '../components/CreateTaskModal';
import BulkActions from '../components/BulkActions';
import { Checkbox } from '../components/ui/checkbox';
import { formatDistanceToNow, parseISO, isPast, isToday, compareAsc } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TasksAssigned = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/assigned-by-me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTasks(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTasks, fetchUsers]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchTasks();
    setIsRefreshing(false);
    toast.success('Tasks refreshed!');
  };

  const handleTaskCreated = () => {
    fetchTasks();
    setShowCreateModal(false);
    toast.success('Task created successfully!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30',
      acknowledged: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
      'in progress': 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30',
      completed: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30'
    };
    return styles[status] || '';
  };

  const getDueDateInfo = (dueDate) => {
    const date = parseISO(dueDate);
    const isOverdue = isPast(date) && !isToday(date);
    const isDueToday = isToday(date);
    return {
      text: formatDistanceToNow(date, { addSuffix: true }),
      isOverdue,
      isDueToday
    };
  };

  const sortTasks = (taskList) => {
    const statusOrder = { 'new': 0, 'in progress': 1, 'completed': 2 };
    return [...taskList].sort((a, b) => {
      const dateA = parseISO(a.due_date);
      const dateB = parseISO(b.due_date);
      const dateCompare = compareAsc(dateA, dateB);
      if (dateCompare !== 0) return dateCompare;
      return (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    });
  };

  const activeTasks = sortTasks(tasks.filter(t => t.status !== 'completed'));
  const completedTasks = sortTasks(tasks.filter(t => t.status === 'completed'));

  const handleTaskSelect = (taskId, e) => {
    e.stopPropagation();
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        const newSelection = prev.filter(id => id !== taskId);
        if (newSelection.length === 0) setSelectionMode(false);
        return newSelection;
      } else {
        if (!selectionMode) setSelectionMode(true);
        return [...prev, taskId];
      }
    });
  };

  const handleSelectAll = (taskList) => {
    const taskIds = taskList.map(t => t.id);
    const allSelected = taskIds.every(id => selectedTasks.includes(id));
    if (allSelected) {
      setSelectedTasks(prev => prev.filter(id => !taskIds.includes(id)));
    } else {
      setSelectedTasks(prev => [...new Set([...prev, ...taskIds])]);
    }
    setSelectionMode(true);
  };

  const clearSelection = () => {
    setSelectedTasks([]);
    setSelectionMode(false);
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b">
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="pl-10 lg:pl-0">
              <h1 className="font-heading text-xl sm:text-2xl font-bold">Tasks I Assigned</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {activeTasks.length} active, {completedTasks.length} completed
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="hidden sm:flex"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="sm:hidden"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button onClick={() => setShowCreateModal(true)} data-testid="new-task-btn" size="sm" className="sm:size-default">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Task</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Active Tasks */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">
                  Active Tasks
                  <Badge variant="secondary" className="ml-2">{activeTasks.length}</Badge>
                </CardTitle>
                {activeTasks.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSelectAll(activeTasks)}
                    className="text-xs"
                    data-testid="select-all-active"
                  >
                    {activeTasks.every(t => selectedTasks.includes(t.id)) ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                {activeTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <User className="w-12 h-12 mb-4 opacity-20" />
                    <p>No active tasks assigned by you</p>
                    <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Task
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeTasks.map((task) => {
                      const dueInfo = getDueDateInfo(task.due_date);
                      const isSelected = selectedTasks.includes(task.id);
                      return (
                        <div
                          key={task.id}
                          className={`p-4 rounded-xl border bg-card cursor-pointer transition-all ${
                            isSelected ? 'ring-2 ring-primary' : 'hover:bg-accent/50'
                          }`}
                          onClick={() => !selectionMode && navigate(`/tasks/${task.id}`)}
                          data-testid={`assigned-task-${task.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleTaskSelect(task.id, { stopPropagation: () => {} })}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium truncate mb-1">{task.name}</h3>
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {task.description || 'No description'}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className={`flex items-center gap-1 ${dueInfo.isOverdue ? 'text-red-500' : ''}`}>
                                  <Clock className="w-3 h-3" />
                                  {dueInfo.text}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {task.assigned_to_name}
                                </span>
                                {task.secondary_participants?.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    +{task.secondary_participants.length}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge variant="outline" className={getStatusBadge(task.status)}>
                                {task.status}
                              </Badge>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Completed Tasks */}
          {completedTasks.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg">
                  Completed
                  <Badge variant="secondary" className="ml-2">{completedTasks.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-3">
                    {completedTasks.map((task) => {
                      const dueInfo = getDueDateInfo(task.due_date);
                      return (
                        <div
                          key={task.id}
                          className="p-4 rounded-xl border bg-card/50 cursor-pointer hover:bg-accent/30 transition-colors opacity-70"
                          onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium truncate line-through">{task.name}</h3>
                              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                <span>{dueInfo.text}</span>
                                <span>•</span>
                                <span>{task.assigned_to_name}</span>
                              </div>
                            </div>
                            <Badge variant="outline" className={getStatusBadge(task.status)}>
                              completed
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </main>

        {/* Create Task Modal */}
        <CreateTaskModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTaskCreated={handleTaskCreated}
        />

        {/* Bulk Actions Bar */}
        <BulkActions
          selectedTasks={selectedTasks}
          onClearSelection={clearSelection}
          onActionComplete={() => fetchTasks()}
          token={token}
          users={allUsers}
        />
      </div>
    </Sidebar>
  );
};

export default TasksAssigned;
